import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Typography, Divider, Button, Space, Popconfirm } from "antd";
import AdnInput from "../common/adnInput/AdnInput";
import QuizGroupForm from "./QuizGroupForm";
import AdnCancelButton from "../common/adnCancelButton/AdnCancelButton";
import QuizQuestionOpenAnswerForm from "./QuizQuestionOpenAnswerForm";
import QuizQuestionTrueFalseForm from "./QuizQuestionTrueFalseForm";
import QuizQuestionOneChoiceForm from "./QuizQuestionOneChoiceForm";
import QuizQuestionMultipleChoiceForm from "./QuizQuestionMultipleChoiceForm";
import QuizQuestionScaleForm from "./QuizQuestionScaleForm";
import QuizQuestionDateForm from "./QuizQuestionDateForm";
import {
  AnswerType,
  EmployeeFollowUpEventQuizStatus,
  dateFormat,
  NotApplicableSuffix,
  ErrorSuffix,
  EmployeeSuffix,
  AnsweredBy,
} from "../common/GeneralConfigurations";
import "./QuizAnswerForm.css";
import CaptionComponent, {
  CaptionType,
} from "../common/adnCaptionComponent/AdnCaptionComponent";
import { saveQuizAnswersToStorage } from "../../utils/quizStorageUtils";

const { Text } = Typography;

const QuizAnswerForm = ({
  iAmSupervisor,
  readonly,
  eventTypeName,
  consultantName,
  supervisorName,
  quizStatusId,
  quizStatusDescription,
  submitLoading,
  handleCancel,
  handleSaveQuiz,
  quizData,
  quizAnswersValues = {},
  handleAssignToSupervisor,
  isUserRH,
  setShowMessage,
  token,
  storageKey,
}) => {
  const messageAnswerIsRequired = "Answer is required.";
  const [answeredBy, setAnsweredBy] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [forceRerender, setForceRerender] = useState(0);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (quizData) {
      // checks if there are at least one question that has to be answered by the supervisor
      quizData.groups.map((grp) => {
        const hasAny = grp.questions.filter((qst) => qst.answeredBy === true);
        if (hasAny.length > 0) {
          setAnsweredBy(true);
        }
      });
    }
  }, [quizStatusId]);

  useEffect(() => {
    if (quizAnswersValues && Object.keys(quizAnswersValues).length > 0) {
      setQuizAnswers(quizAnswersValues);
    }
  }, [quizAnswersValues]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const identifier = storageKey || token;
    if (readonly || !identifier || !quizData) {
      return;
    }

    const hasAnswers = quizAnswers && Object.keys(quizAnswers).length > 0;
    if (!hasAnswers) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveQuizAnswersToStorage(identifier, quizData.answerId, quizAnswers);
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [quizAnswers, token, storageKey, quizData, readonly]);

  const handleAutosave = () => {
    const identifier = storageKey || token;
    if (readonly || !identifier || !quizData) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveQuizAnswersToStorage(identifier, quizData.answerId, quizAnswers);
    }, 500);
  };

  const handleSubmitQuiz = async () => {
    setFieldValue("isSubmiting", true);
    validateFormForErrors();
    if (formHasErrors() === false) {
      handleSaveQuiz(quizAnswers, true);
    }
  };

  const handleSave = () => {
    setFieldValue("isSubmiting", false);
    validateFormForErrors();
    if (formHasErrors() === false) {
      handleSaveQuiz(quizAnswers);
    }
  };

  const validateFormForErrors = () => {
    const validationAnswers =
      quizStatusId ===
      EmployeeFollowUpEventQuizStatus.AwaitsResponseFromSupervisor
        ? quizAnswers.supervisorAnswer
        : quizAnswers;

    for (let key in validationAnswers) {
      if (validationAnswers.hasOwnProperty(key)) {
        const keySplit = key.split("_");
        if (keySplit.length === 4) {
          handleQuestionValidation(keySplit[3], key, validationAnswers[key]);
        }
      }
    }
  };

  const formHasErrors = () => {
    for (const [key, value] of Object.entries(quizAnswers)) {
      if (
        key.endsWith(ErrorSuffix) &&
        value != null &&
        value != undefined &&
        value != ""
      ) {
        return true;
      }
    }
    return false;
  };

  const handleQuestionValidation = (questionId, name, value) => {
    if (quizData) {
      quizData.groups.forEach((group) => {
        const question = group.questions.filter((x) => x.id === +questionId);
        // question exists
        if (question.length > 0) {
          if (
            question[0].isRequired === true &&
            (value === null || value === undefined || value === "")
          ) {
            // if not applicable is selected...
            if (
              question[0].allowNotApplicable === false ||
              (question[0].allowNotApplicable === true &&
                getFieldValue(name.concat(NotApplicableSuffix)) === false &&
                question[0].answerTypeId !== AnswerType.VerdadeiroFalso)
            ) {
              setErrorMessage(name, messageAnswerIsRequired);
            } else {
              setErrorMessage(name, null);
            }
            return;
          }
          if (
            (question[0].answerTypeId === AnswerType.EscolhaMultipla &&
              value !== null &&
              value !== undefined) ||
            value === ""
          ) {
            if (value.length !== +question[0].minValue) {
              setErrorMessage(name, `Choose ${question[0].minValue} options.`);
            } else {
              setErrorMessage(name, null);
            }
            return;
          } else if (
            (question[0].answerTypeId === AnswerType.Data &&
              value !== null &&
              value !== undefined) ||
            value === ""
          ) {
            if (
              moment(value, dateFormat).isBefore(
                moment(question[0].minValue, dateFormat)
              )
            ) {
              setErrorMessage(
                name,
                `Date has to be greater or equal to ${question[0].minValue}.`
              );
            } else if (
              moment(value, dateFormat).isAfter(
                moment(question[0].maxValue, dateFormat)
              )
            ) {
              setErrorMessage(
                name,
                `Date has to be less or equal to ${question[0].maxValue}.`
              );
            } else {
              setErrorMessage(name, null);
            }
            return;
          } else {
            setErrorMessage(name, null);
            return;
          }
        }
      });
    }
  };

  const setFieldName = (
    group,
    question,
    isNotApplicableColumn = false,
    isEmployee = false
  ) => {
    let name = `grp_${group}_qst_${question}`;
    if (isEmployee === true) {
      name += `${EmployeeSuffix}`;
    }
    if (isNotApplicableColumn === true) {
      name += `${NotApplicableSuffix}`;
    }
    return name;
  };
  const getSupervisorFieldName = (
    group,
    question,
    isNotApplicableColumn = false
  ) => {
    let name = `grp_${group}_qst_${question}`;
    if (isNotApplicableColumn === true) {
      name = `${name}${NotApplicableSuffix}`;
    }
    return name;
  };
  const getEmployeeFieldName = (
    group,
    question,
    isNotApplicableColumn = false
  ) => {
    let name = getSupervisorFieldName(group, question);
    name = `${name}${EmployeeSuffix}`;
    if (isNotApplicableColumn === true) {
      name = `${name}${NotApplicableSuffix}`;
    }
    return name;
  };
  const setFieldValue = (name, value) => {
    const newAnswers = { ...quizAnswers, [name]: value };
    setQuizAnswers(newAnswers);
  };
  const getFieldValue = (name) => {
    const value = quizAnswers[name];
    return value;
  };
  const setErrorMessage = (name, message) => {
    setFieldValue(name.concat(ErrorSuffix), message);
    if (message !== null && message !== undefined) {
    }
    setForceRerender(forceRerender + 1);
  };
  const getErrorMessage = (groupid, questionId) => {
    return getFieldValue(setFieldName(groupid, questionId).concat(ErrorSuffix));
  };

  const handleChange = (event) => {
    const newAnswers = { ...quizAnswers };
    newAnswers[event.target.name] =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setQuizAnswers(newAnswers);
  };
  const handleSwitchChange = (checked, name) => {
    const newAnswers = { ...quizAnswers };
    newAnswers[name] = checked;
    setQuizAnswers(newAnswers);
  };
  const handleSelectChange = (value, name) => {
    const newAnswers = { ...quizAnswers };
    newAnswers[name] = value;
    setQuizAnswers(newAnswers);
  };
  const handleDateChange = (date, name) => {
    const newAnswers = { ...quizAnswers };
    newAnswers[name] = date !== null ? date.format(dateFormat) : date;
    setQuizAnswers(newAnswers);
  };

  const onNotApplicable = (value, fieldName) => {
    if (value.target.checked === true) {
      setFieldValue(fieldName, undefined);
    }
    handleChange(value);
    if (iAmSupervisor) {
      handleAutosave();
    }
  };

  const isReadOnly = (value) => {
    if (
      (iAmSupervisor === true &&
        quizStatusId ===
          EmployeeFollowUpEventQuizStatus.AwaitsResponseFromConsultant) ||
      (iAmSupervisor === false &&
        quizStatusId ===
          EmployeeFollowUpEventQuizStatus.AwaitsResponseFromSupervisor)
    ) {
      return true;
    } else {
      return value;
    }
  };

  const isSubgroup = (groupOrder) => {
    return Number.isInteger(groupOrder) ? false : true;
  };

  return (
    <>
      <Row justify={consultantName ? "space-between" : "start"}>
        <Col>
          <h5>{eventTypeName}</h5>
        </Col>
        {consultantName && (
          <Col>
            <Row>
              <Col>
                <h5 style={{ color: "var(--adnblue)" }}>{consultantName}</h5>
              </Col>
            </Row>
          </Col>
        )}
      </Row>

      <AdnInput
        className="hidden-input"
        name="isSubmiting"
        type="hidden"
        value="false"
        style={{ padding: 0 }}
      />

      <Row className="quiz-data">
        <Col xs={24}>
          <Row>
            <h6>Quiz Data:</h6>
          </Row>
          <Row align="middle">
            {quizData && quizData.quizReference && (
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={6}>
                <Row>
                  <Col>
                    <p className="data-title">Manager: </p>
                  </Col>
                  <Col>
                    <p className="data-value">{quizData.managerName}</p>
                  </Col>
                </Row>
              </Col>
            )}
            {supervisorName && (
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={6}>
                <Row>
                  <Col>
                    <p className="data-title">Supervised by: </p>
                  </Col>
                  <Col>
                    <p className="data-value">{supervisorName}</p>
                  </Col>
                </Row>
              </Col>
            )}
            {quizData && quizData.dateSent && (
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={6}>
                <Row>
                  <Col>
                    <p className="data-title">Sent Date: </p>
                  </Col>
                  <Col>
                    <p className="data-value">
                      {quizData.dateSent.substring(0, 10)}
                    </p>
                  </Col>
                </Row>
              </Col>
            )}
            {quizData && quizData.admissionDate && (
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={6}>
                <Row>
                  <Col>
                    <p className="data-title">Admission Date: </p>
                  </Col>
                  <Col>
                    <p className="data-value">
                      {quizData.admissionDate.substring(0, 10)}
                    </p>
                  </Col>
                </Row>
              </Col>
            )}
            {quizData && quizData.dateAnswered && (
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={6}>
                <Row>
                  <Col>
                    <p className="data-title">Consultant Answered at: </p>
                  </Col>
                  <Col>
                    <p className="data-value">
                      {quizData.dateAnswered.substring(0, 10)}
                    </p>
                  </Col>
                </Row>
              </Col>
            )}
            {quizData && quizData.dateAnsweredBySupervisor && (
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={6}>
                <Row>
                  <Col>
                    <p className="data-title">Supervisor Answered at: </p>
                  </Col>
                  <Col>
                    <p className="data-value">
                      {quizData.dateAnsweredBySupervisor.substring(0, 10)}
                    </p>
                  </Col>
                </Row>
              </Col>
            )}
            {quizStatusDescription && (
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={6}>
                <CaptionComponent
                  text={quizStatusDescription}
                  status={CaptionType.Information}
                />
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      {isUserRH &&
        quizStatusId ===
          EmployeeFollowUpEventQuizStatus.AwaitsResponseFromConsultant &&
        iAmSupervisor === true && (
          <Row justify="end">
            <Col>
              <Popconfirm
                title="Are you sure you want to change the quiz status? The consultant will no longer be allowed to respond to the quiz."
                okText="Yes"
                cancelText="No"
                onConfirm={() => handleAssignToSupervisor()}
              >
                <Button className="quizAnswerButton adn-primary-button">
                  Assign to Supervisor
                </Button>
              </Popconfirm>
            </Col>
          </Row>
        )}

      {quizData === null && (
        <>
          <Text strong style={{ color: "#ffc222" }}>
            No published quiz is available.
          </Text>
          <br />
        </>
      )}

      {quizStatusId !== EmployeeFollowUpEventQuizStatus.NoQuiz && (
        <>
          {quizData &&
            quizData.groups.map((grp, key1) => (
              <div key={`grp${key1}`}>
                <Col
                  xs={24}
                  style={
                    grp && !isSubgroup(grp.order)
                      ? {
                          border: "2px solid var(--adnblue)",
                          padding: "1rem",
                          borderRadius: "16px",
                          marginBottom: "1rem",
                        }
                      : {
                          border: "2px solid var(--adnlightblue)",
                          padding: "1rem",
                          borderRadius: "16px",
                          marginLeft: "16px",
                          marginBottom: "1rem",
                        }
                  }
                >
                  {/* Group */}
                  <QuizGroupForm
                    description={grp.description}
                    note={grp.note}
                  />

                  {/* Question */}
                  {grp &&
                    grp.questions &&
                    grp.questions.map((qst, key2) => (
                      <Row
                        key={`qst${key2}`}
                        gutter={16}
                        className="quiz-question"
                      >
                        {qst.answerTypeId === AnswerType.RespostaAberta && (
                          <Col xs={24}>
                            <QuizQuestionOpenAnswerForm
                              quizIsAnsweredBy={quizData.isAnsweredByConsultant}
                              quizStatusId={quizStatusId}
                              readonly={readonly}
                              answeredBy={qst.answeredBy}
                              questionText={qst.name}
                              isRequired={qst.isRequired}
                              allowNotApplicable={qst.allowNotApplicable}
                              errorMessage={getErrorMessage(grp.id, qst.id)}
                              consultantAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id)
                                      : getSupervisorFieldName(grp.id, qst.id)
                              )}
                              consultantAllowNotApplicableAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id, true)
                                      : getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              supervisorAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id)
                              )}
                              supervisorAllowNotApplicableAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              fieldName={setFieldName(grp.id, qst.id)}
                              handleChange={handleChange}
                              onNotApplicable={onNotApplicable}
                              isReadOnly={isReadOnly}
                              onBlur={() => handleAutosave()}
                            />
                          </Col>
                        )}

                        {qst.answerTypeId === AnswerType.VerdadeiroFalso && (
                          <Col xs={24}>
                            <QuizQuestionTrueFalseForm
                              quizIsAnsweredBy={quizData.isAnsweredByConsultant}
                              quizStatusId={quizStatusId}
                              readonly={readonly}
                              answeredBy={qst.answeredBy}
                              questionText={qst.name}
                              isRequired={qst.isRequired}
                              allowNotApplicable={qst.allowNotApplicable}
                              errorMessage={getErrorMessage(grp.id, qst.id)}
                              consultantAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id)
                                      : getSupervisorFieldName(grp.id, qst.id)
                              )}
                              consultantAllowNotApplicableAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id, true)
                                      : getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              supervisorAnswer={
                                getFieldValue(
                                  getSupervisorFieldName(grp.id, qst.id)
                                ) ?? false
                              }
                              supervisorAllowNotApplicableAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              fieldName={setFieldName(grp.id, qst.id)}
                              handleSwitchChange={handleSwitchChange}
                              onNotApplicable={onNotApplicable}
                              isReadOnly={isReadOnly}
                              onBlur={() => handleAutosave()}
                            />
                          </Col>
                        )}

                        {qst.answerTypeId === AnswerType.EscolhaUnica && (
                          <Col xs={24}>
                            <QuizQuestionOneChoiceForm
                              quizIsAnsweredBy={quizData.isAnsweredByConsultant}
                              quizStatusId={quizStatusId}
                              readonly={readonly}
                              answeredBy={qst.answeredBy}
                              questionText={qst.name}
                              isRequired={qst.isRequired}
                              allowNotApplicable={qst.allowNotApplicable}
                              errorMessage={getErrorMessage(grp.id, qst.id)}
                              questionDomainOptions={qst.questionDomainOptions}
                              consultantAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id)
                                      : getSupervisorFieldName(grp.id, qst.id)
                              )}
                              consultantAllowNotApplicableAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id, true)
                                      : getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              supervisorAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id)
                              )}
                              supervisorAllowNotApplicableAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              fieldName={setFieldName(grp.id, qst.id)}
                              handleChange={handleChange}
                              onNotApplicable={onNotApplicable}
                              isReadOnly={isReadOnly}
                              handleSelectChange={handleSelectChange}
                              onBlur={() => handleAutosave()}
                            />
                          </Col>
                        )}

                        {qst.answerTypeId === AnswerType.EscolhaMultipla &&
                          qst.questionDomainOptions &&
                          qst.questionDomainOptions.length > 0 && (
                            <Col xs={24}>
                              <QuizQuestionMultipleChoiceForm
                                quizIsAnsweredBy={
                                  quizData.isAnsweredByConsultant
                                }
                                quizStatusId={quizStatusId}
                                readonly={readonly}
                                answeredBy={qst.answeredBy}
                                quizLanguage={quizData.quizLanguage}
                                minValue={qst.minValue}
                                questionText={qst.name}
                                isRequired={qst.isRequired}
                                allowNotApplicable={qst.allowNotApplicable}
                                errorMessage={getErrorMessage(grp.id, qst.id)}
                                questionDomainOptions={
                                  qst.questionDomainOptions
                                }
                                consultantAnswer={getFieldValue(
                                  iAmSupervisor
                                    ? getEmployeeFieldName(grp.id, qst.id)
                                    : getSupervisorFieldName(grp.id, qst.id)
                                )}
                                consultantAllowNotApplicableAnswer={getFieldValue(
                                  iAmSupervisor
                                    ? getEmployeeFieldName(grp.id, qst.id, true)
                                    : getSupervisorFieldName(
                                        grp.id,
                                        qst.id,
                                        true
                                      )
                                )}
                                supervisorAnswer={getFieldValue(
                                  getSupervisorFieldName(grp.id, qst.id)
                                )}
                                supervisorAllowNotApplicableAnswer={getFieldValue(
                                  getSupervisorFieldName(grp.id, qst.id, true)
                                )}
                                fieldName={setFieldName(grp.id, qst.id)}
                                handleSelectChange={handleSelectChange}
                                onNotApplicable={onNotApplicable}
                                isReadOnly={isReadOnly}
                                onBlur={() => handleAutosave()}
                              />
                            </Col>
                          )}

                        {qst.answerTypeId === AnswerType.Data && (
                          <Col xs={24}>
                            <QuizQuestionDateForm
                              quizIsAnsweredBy={quizData.isAnsweredByConsultant}
                              quizStatusId={quizStatusId}
                              readonly={readonly}
                              answeredBy={qst.answeredBy}
                              questionText={qst.name}
                              isRequired={qst.isRequired}
                              quizLanguage={quizData.quizLanguage}
                              allowNotApplicable={qst.allowNotApplicable}
                              errorMessage={getErrorMessage(grp.id, qst.id)}
                              consultantAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id)
                                      : getSupervisorFieldName(grp.id, qst.id)
                              )}
                              consultantAllowNotApplicableAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id, true)
                                      : getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              supervisorAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id)
                              )}
                              supervisorAllowNotApplicableAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              fieldName={setFieldName(grp.id, qst.id)}
                              handleDateChange={handleDateChange}
                              onNotApplicable={onNotApplicable}
                              isReadOnly={isReadOnly}
                              onBlur={() => handleAutosave()}
                            />
                          </Col>
                        )}

                        {qst.answerTypeId === AnswerType.Escala && (
                          <Col xs={24}>
                            <QuizQuestionScaleForm
                              quizIsAnsweredBy={quizData.isAnsweredByConsultant}
                              quizStatusId={quizStatusId}
                              readonly={readonly}
                              answeredBy={qst.answeredBy}
                              questionText={qst.name}
                              isRequired={qst.isRequired}
                              minValue={qst.minValue}
                              maxValue={qst.maxValue}
                              allowNotApplicable={qst.allowNotApplicable}
                              errorMessage={getErrorMessage(grp.id, qst.id)}
                              consultantAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id)
                                      : getSupervisorFieldName(grp.id, qst.id)
                              )}
                              consultantAllowNotApplicableAnswer={getFieldValue(
                                  iAmSupervisor
                                      ? getEmployeeFieldName(grp.id, qst.id, true)
                                      : getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              supervisorAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id)
                              )}
                              supervisorAllowNotApplicableAnswer={getFieldValue(
                                getSupervisorFieldName(grp.id, qst.id, true)
                              )}
                              fieldName={setFieldName(grp.id, qst.id)}
                              handleSliderChange={handleSelectChange}
                              onNotApplicable={onNotApplicable}
                              isReadOnly={isReadOnly}
                              onBlur={() => handleAutosave()}
                            />
                          </Col>
                        )}
                      </Row>
                    ))}
                </Col>
              </div>
            ))}

          <br />
          {quizStatusId !== EmployeeFollowUpEventQuizStatus.Answered &&
            quizData !== null &&
            readonly === false && (
              <Row>
                <Col xs={24} md={12}>
                  {readonly || (
                    <Button
                      className="adn-primary-button primary-secundary-color"
                      onClick={handleSubmitQuiz}
                      loading={submitLoading}
                    >
                      Submit Quiz
                    </Button>
                  )}
                </Col>
                <Col
                  xs={24}
                  md={12}
                  justify="end"
                  style={{ flex: 1, maxWidth: "none" }}
                >
                  {quizData &&
                  quizStatusId !==
                    EmployeeFollowUpEventQuizStatus.AwaitsResponseFromConsultant ? (
                    <Row justify="end">
                      <Col
                        xs={24}
                        sm={24}
                        md={6}
                        lg={4}
                        xl={3}
                        xxl={3}
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <AdnCancelButton onClick={handleCancel} />
                      </Col>
                      <Col
                        xs={24}
                        sm={24}
                        md={6}
                        lg={4}
                        xl={3}
                        xxl={3}
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        {readonly || (
                          <button
                            className="adn-primary-button"
                            onClick={handleSave}
                            loading={submitLoading}
                          >
                            <span>Save</span>
                          </button>
                        )}
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col
                        xs={24}
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        {readonly || (
                          <button
                            className="adn-primary-button"
                            onClick={handleSave}
                            loading={submitLoading}
                          >
                            <span>Save</span>
                          </button>
                        )}
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            )}
        </>
      )}
    </>
  );
};

export default QuizAnswerForm;
